import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  typeMaterielService,
  type CreateTypeMaterielDTO,
  type TypeMateriel,
} from '@/services/typeMaterielService';
import toast from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

/**
 * Referentiel administrable des types de materiel/vehicule (demande
 * explicite de l'utilisateur, remplace la liste fermee codee en dur).
 * Reserve a ROLE_ADMIN : ce referentiel conditionne la validation du
 * champ `type` sur le materiel et sur la grille de compatibilite.
 */
export function TypesMaterielPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState<TypeMateriel | null>(null);
  const [formData, setFormData] = useState<CreateTypeMaterielDTO>({ code: '', libelle: '', actif: true });

  const { data: types, isLoading } = useQuery({
    queryKey: ['types-materiel'],
    queryFn: () => typeMaterielService.list(),
  });

  const invalidateTout = () => {
    queryClient.invalidateQueries({ queryKey: ['types-materiel'] });
    // Les ecrans Materiel et Compatibilite affichent des libelles/colonnes
    // derives de ce referentiel - les rafraichir aussi.
    queryClient.invalidateQueries({ queryKey: ['materiels'] });
    queryClient.invalidateQueries({ queryKey: ['compatibilites'] });
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateTypeMaterielDTO) => typeMaterielService.create(data),
    onSuccess: () => {
      invalidateTout();
      closeModal();
      toast.success('Type cree');
    },
    onError: (error: any) => toast.error(error.response?.data?.error || "Erreur lors de la creation"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateTypeMaterielDTO }) =>
      typeMaterielService.update(id, data),
    onSuccess: () => {
      invalidateTout();
      closeModal();
      toast.success('Type modifie');
    },
    onError: (error: any) => toast.error(error.response?.data?.error || 'Erreur lors de la modification'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => typeMaterielService.delete(id),
    onSuccess: () => {
      invalidateTout();
      toast.success('Type supprime');
    },
    onError: (error: any) => toast.error(error.response?.data?.error || 'Erreur lors de la suppression'),
  });

  const closeModal = () => {
    setShowModal(false);
    setEditingType(null);
    setFormData({ code: '', libelle: '', actif: true });
  };

  const openEditModal = (type: TypeMateriel) => {
    setEditingType(type);
    setFormData({ code: type.code, libelle: type.libelle, actif: type.actif });
    setShowModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingType) {
      updateMutation.mutate({ id: editingType.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (type: TypeMateriel) => {
    if (window.confirm(`Supprimer le type "${type.libelle}" ?`)) {
      deleteMutation.mutate(type.id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement des types de materiel...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Types de materiel</h2>
        <button onClick={() => setShowModal(true)} className="btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          Nouveau type
        </button>
      </div>
      <p className="text-sm text-gray-500">
        Referentiel utilise par les pages Materiel et Compatibilite. Le code d'un type existant
        n'est pas modifiable (deja utilise par du materiel ou des regles de compatibilite) -
        desactivez-le et creez-en un nouveau si besoin.
      </p>

      <div className="card">
        <table className="min-w-full">
          <thead>
            <tr>
              <th>Code</th>
              <th>Libelle</th>
              <th>Actif</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {(types || []).map((type) => (
              <tr key={type.id}>
                <td className="font-mono text-sm">{type.code}</td>
                <td className="font-medium">{type.libelle}</td>
                <td>{type.actif ? 'Oui' : 'Non'}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEditModal(type)} className="p-1 text-gray-400 hover:text-primary-500">
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button onClick={() => handleDelete(type)} className="p-1 text-gray-400 hover:text-red-600">
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {(types || []).length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-gray-500 py-8">Aucun type enregistre</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">{editingType ? 'Modifier le type' : 'Nouveau type'}</h3>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="label">Code *</label>
                  <input
                    type="text"
                    className="input font-mono"
                    value={formData.code}
                    disabled={!!editingType}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="ex: epandeuse_liquide"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Minuscules, chiffres et underscores uniquement.</p>
                </div>
                <div>
                  <label className="label">Libelle *</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.libelle}
                    onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                    required
                  />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.actif ?? true}
                    onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
                  />
                  Actif
                </label>
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-3">
                <button type="button" onClick={closeModal} className="btn-secondary">Annuler</button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingType ? 'Modifier' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TypesMaterielPage;
